import { Action, createReducer, on } from '@ngrx/store';

import * as PlaylistActions from '../actions/playlist.actions';
import { Playlist } from '../models/playlist';
import { SciencePlaylistProblem, EnglishPlaylistProblem, NationalLanguagePlaylistProblem, HistoryPlaylistProblem } from '../models/problem';
import { SubjectId } from '../resources/config';

export const playlistFeatureKey = 'playlist';

export interface ResolvePdfObjectUrlResult {
  success: boolean;
  pdfPath: string;
  objectUrl: string | null;
}

export interface State {
  playlists: Playlist[] | null;

  /** playlist の problem id 達 */
  playlistProblemIds: string[] | null;

  /** 英語の playlist に紐づく問題達 */
  englishPlaylistProblems: EnglishPlaylistProblem[] | null;

  /** 数学の playlist に紐づく問題達 */
  mathPlaylistProblems: SciencePlaylistProblem[] | null;

  /** 国語の playlist に紐づく問題達 */
  nationalLanguagePlaylistProblems: NationalLanguagePlaylistProblem[] | null;

  /** 物理の playlist に紐づく問題達 */
  physicsPlaylistProblems: SciencePlaylistProblem[] | null;

  /** 化学の playlist に紐づく問題達 */
  chemistryPlaylistProblems: SciencePlaylistProblem[] | null;

  /** 生物の playlist に紐づく問題達 */
  biologyPlaylistProblems: SciencePlaylistProblem[] | null;

  /** 日本史の playlist に紐づく問題達 */
  japaneseHistoryPlaylistProblems: HistoryPlaylistProblem[] | null;

  /** 世界史の playlist に紐づく問題達 */
  worldHistoryPlaylistProblems: HistoryPlaylistProblem[] | null;

  /** 地理の playlist に紐づく問題達 */
  geographyPlaylistProblems: SciencePlaylistProblem[] | null;

  /** 政治経済の playlist に紐づく問題達 */
  politicalEconomyPlaylistProblems: SciencePlaylistProblem[] | null;

  /** 推薦・総合の playlist に紐づく問題達 */
  specialAdmissionsPlaylistProblems: SciencePlaylistProblem[] | null;

  /**
   * 各問題の PDF を ObjectURL に変換した結果保持用
   * 複数同時ダウンロードに対応するために配列になっています
   */
  resolvePdfObjectUrlResults: ResolvePdfObjectUrlResult[];
}

export const initialState: State = {
  playlists: null,
  playlistProblemIds: null,
  englishPlaylistProblems: null,
  mathPlaylistProblems: null,
  nationalLanguagePlaylistProblems: null,
  physicsPlaylistProblems: null,
  chemistryPlaylistProblems: null,
  biologyPlaylistProblems: null,
  japaneseHistoryPlaylistProblems: null,
  worldHistoryPlaylistProblems: null,
  geographyPlaylistProblems: null,
  politicalEconomyPlaylistProblems: null,
  specialAdmissionsPlaylistProblems: null,
  resolvePdfObjectUrlResults: []
};

const playlistReducer = createReducer(
  initialState,

  // Find Playlists -------------------------------------------------------
  on(PlaylistActions.findPlaylistsSuccess, (state, { playlists }) => ({ ...state, playlists })),

  // Find Playlist Problem ids -------------------------------------------------------
  on(PlaylistActions.findPlaylistProblemIdsSuccess, (state, { playlistProblemIds }) => ({ ...state, playlistProblemIds })),
  on(PlaylistActions.initializePlaylistProblemIdsState, state => {
    const next: State = { ...state, playlistProblemIds: null };
    return next;
  }),

  // Find Playlist Problems -------------------------------------------------------
  on(PlaylistActions.findPlaylistProblemsSuccess, (state, { subjectId, playlistProblems }) => {
    switch (subjectId) {
      case SubjectId.ENGLISH:
        return { ...state, englishPlaylistProblems: playlistProblems as EnglishPlaylistProblem[] };
      case SubjectId.MATH:
        return { ...state, mathPlaylistProblems: playlistProblems as SciencePlaylistProblem[] };
      case SubjectId.NATIONAL_LANGUAGE:
        return { ...state, nationalLanguagePlaylistProblems: playlistProblems as NationalLanguagePlaylistProblem[] };
      case SubjectId.PHYSICS:
        return { ...state, physicsPlaylistProblems: playlistProblems as SciencePlaylistProblem[] };
      case SubjectId.CHEMISTRY:
        return { ...state, chemistryPlaylistProblems: playlistProblems as SciencePlaylistProblem[] };
      case SubjectId.BIOLOGY:
        return { ...state, biologyPlaylistProblems: playlistProblems as SciencePlaylistProblem[] };
      case SubjectId.JAPANESE_HISTORY:
        return { ...state, japaneseHistoryPlaylistProblems: playlistProblems as HistoryPlaylistProblem[] };
      case SubjectId.WORLD_HISTORY:
        return { ...state, worldHistoryPlaylistProblems: playlistProblems as HistoryPlaylistProblem[] };
      case SubjectId.GEOGRAPHY:
        return { ...state, geographyPlaylistProblems: playlistProblems as SciencePlaylistProblem[] };
      case SubjectId.POLITICAL_ECONOMY:
        return { ...state, politicalEconomyPlaylistProblems: playlistProblems as SciencePlaylistProblem[] };
      case SubjectId.SPECIAL_ADMISSIONS:
        return { ...state, specialAdmissionsPlaylistProblems: playlistProblems as SciencePlaylistProblem[] };
      default:
        return state;
    }
  }),
  on(PlaylistActions.initializePlaylistProblemsState, state => {
    const next: State = {
      ...state,
      englishPlaylistProblems: null,
      mathPlaylistProblems: null,
      nationalLanguagePlaylistProblems: null,
      physicsPlaylistProblems: null,
      chemistryPlaylistProblems: null,
      biologyPlaylistProblems: null,
      japaneseHistoryPlaylistProblems: null,
      worldHistoryPlaylistProblems: null,
      geographyPlaylistProblems: null,
      politicalEconomyPlaylistProblems: null,
      specialAdmissionsPlaylistProblems: null
    };
    return next;
  }),

  // Resolve Pdf Object Url -------------------------------------------------------
  on(PlaylistActions.resolvePdfObjectUrlSuccess, (state, { pdfPath, objectUrl }) => {
    const resolvePdfObjectUrlResults: ResolvePdfObjectUrlResult[] = [
      ...state.resolvePdfObjectUrlResults,
      { success: true, pdfPath, objectUrl }
    ];
    return { ...state, resolvePdfObjectUrlResults };
  }),
  on(PlaylistActions.resolvePdfObjectUrlFailure, (state, { pdfPath }) => {
    const resolvePdfObjectUrlResults: ResolvePdfObjectUrlResult[] = [
      ...state.resolvePdfObjectUrlResults,
      { success: false, pdfPath, objectUrl: null }
    ];
    return { ...state, resolvePdfObjectUrlResults };
  }),
  on(PlaylistActions.initializePdfObjectUrl, (state, { pdfPath }) => {
    const resolvePdfObjectUrlResults = state.resolvePdfObjectUrlResults.filter(it => it.pdfPath !== pdfPath);
    return { ...state, resolvePdfObjectUrlResults };
  }),
  on(PlaylistActions.initializePdfObjectUrlState, state => {
    const next: State = { ...state, resolvePdfObjectUrlResults: [] };
    return next;
  })
);

export function reducer(state: State | undefined, action: Action) {
  return playlistReducer(state, action);
}
